import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CopyButton, TextInput } from '@opendate-app/setlist';

const axios = require('axios').default;

const TOAST_CONFIG = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  closeOnClick: false,
  autoClose: 5000,
  hideProgressBar: true
};

const ShareLinkModal = ({ title, description, endpoint, resourceKey, csrfToken, onClose, showAccessLevel }) => {
  const [loading, setLoading] = useState(true);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareAccessLevel, setShareAccessLevel] = useState("read");
  const [shareUrl, setShareUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(endpoint)
      .then(({ data }) => {
        setShareEnabled(data.share_enabled);
        setShareAccessLevel(data.share_access_level || "read");
        setShareUrl(data.share_url);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load share settings", TOAST_CONFIG);
        setLoading(false);
      });
  }, [endpoint]);

  const patchShare = (params) => {
    setSaving(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.patch(endpoint, { [resourceKey]: params })
      .then(({ data }) => {
        setShareEnabled(data.share_enabled);
        setShareAccessLevel(data.share_access_level || "read");
        setShareUrl(data.share_url);
        setSaving(false);
      })
      .catch(() => {
        toast.error("Failed to update share settings", TOAST_CONFIG);
        setSaving(false);
      });
  };

  const handleToggle = (enabled) => {
    const params = { share_enabled: enabled };
    if (showAccessLevel) params.share_access_level = shareAccessLevel;
    patchShare(params);
  };

  const handleAccessLevelChange = (accessLevel) => {
    patchShare({ share_enabled: true, share_access_level: accessLevel });
  };


  const toggleId = `share-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`;

  const header = (
    <div className="row">
      <div className="col">
        <h5 className="mb-0">
          <strong>
            <i className="far fa-link text-primary mr-2"></i>
            {title}
          </strong>
        </h5>
      </div>
      <div className="col-xs-auto">
        <a href="#" className="text-dark popup-close-btn" onClick={(e) => { e.preventDefault(); onClose(); }}>
          <i className="far fa-times"></i>
        </a>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div>
        {header}
        <div className="text-center py-4">
          <div className="css-spinner css-spinner--lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between">
          <label className="mb-0" htmlFor={toggleId}>
            <strong>Enable link sharing</strong>
          </label>
          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id={toggleId}
              checked={shareEnabled}
              disabled={saving}
              onChange={(e) => handleToggle(e.target.checked)}
            />
            <label className="custom-control-label" htmlFor={toggleId}></label>
          </div>
        </div>
        <p className="text-muted small mt-1 mb-3">
          {description}
        </p>
      </div>
      {shareEnabled ? (
        <div>
          {showAccessLevel ? (
            <div className="mb-3">
              <label className="mb-1"><strong>Access level</strong></label>
              <select
                className="form-control form-control-sm"
                value={shareAccessLevel}
                disabled={saving}
                onChange={(e) => handleAccessLevelChange(e.target.value)}
              >
                <option value="read">View only</option>
                <option value="upload">Can upload files</option>
              </select>
            </div>
          ) : null}
          <div className="mb-2">
            <label className="mb-1"><strong>Share link</strong></label>
            <TextInput
              size="sm"
              aria-label="Share link"
              value={shareUrl || ""}
              readOnly
              trailingButton={(
                <CopyButton
                  size="sm"
                  label="Copy Link"
                  value={shareUrl || ""}
                  disabled={!shareUrl}
                  onError={() => toast.error("Failed to copy link to clipboard", { ...TOAST_CONFIG, autoClose: 3000 })}
                />
              )}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

ShareLinkModal.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  resourceKey: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  showAccessLevel: PropTypes.bool
};

ShareLinkModal.defaultProps = {
  showAccessLevel: false
};

export default ShareLinkModal;
